import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';

import Navbar from 'components/common/Navbar';
import PrivateRoute from 'components/common/PrivateRoute';
import { ROUTES } from 'constants/routes';
import ErrorBoundary from 'components/common/ErrorBoundary';
import { FOOTER_HEIGHT } from 'common/theme';

import { useStyles } from './styles';

/**
 * Common layout for page with Navbar header
 * @param {string|node} header - title (text or component)
 * @param {boolean} hasAccess - if user has access
 * @param {React.Component} Component - component to display
 * @param {Object} location - current react router location
 * @param {string} toolLink - tool data
 * @param {boolean} fullWidth - fullwidth mode
 * @param {boolean} forwardLocation - if forward location to component
 * @param {Object} restProps
 * @returns {jsx}
 */
function CommonLayout({
  header,
  hasAccess,
  component: Component,
  location,
  toolLink,
  fullWidth,
  forwardLocation,
  ...restProps
}) {
  const classes = useStyles();
  const componentProps = forwardLocation ? { location } : {};
  const Viewport = fullWidth ? Box : Container;
  const year = new Date().getUTCFullYear();

  return (
    <PrivateRoute
      hasAccess={hasAccess}
      redirectTo={ROUTES.forbidden}
      location={location}
      {...restProps}
    >
      <Navbar toolLink={toolLink} />
      <Viewport
        maxWidth={fullWidth ? '100vw' : 'md'}
        style={{ height: '100%' }}
      >
        <ErrorBoundary>
          <div className={classes.content}>
            <Component {...componentProps} />
          </div>
        </ErrorBoundary>
        <Box
          height={FOOTER_HEIGHT}
          width="100%"
          bgcolor="background.default"
          position="fixed"
          bottom={0}
          left={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          Copyright {year} by Livesystems dooh AG
        </Box>
      </Viewport>
    </PrivateRoute>
  );
}

CommonLayout.propTypes = {
  toolLink: PropTypes.shape({
    to: PropTypes.string,
    icon: PropTypes.node,
    text: PropTypes.string,
  }),
  hasAccess: PropTypes.bool,
  forwardLocation: PropTypes.bool,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  location: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
  component: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
    PropTypes.node,
  ]).isRequired,
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  fullWidth: PropTypes.bool,
};

CommonLayout.defaultProps = {
  header: '',
  fullWidth: false,
  forwardLocation: false,
};

export default memo(CommonLayout);
